// src/views/BonosView.jsx
import { VERSION_STRING } from '../version';
import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { useUser } from '../contexts/UserContext';
import { useWallet } from '../contexts/WalletContext'; // For generic loading states if needed
import ReferralWidget from '../components/ReferralWidget';
import { ethers } from 'ethers';
import { FaHistory, FaLock, FaUnlock, FaCheckCircle, FaExternalLinkAlt } from 'react-icons/fa';

import { db, functions, auth } from '../firebaseConfig'; // Added functions and auth
import { httpsCallable } from 'firebase/functions'; // Added httpsCallable
import { collection, query, where, getDocs } from 'firebase/firestore'; // Firestore v9

const BonosView = () => {
    const { wallet, crowdsaleContract } = useWeb3();
    const { userData } = useUser();
    const [referralEvents, setReferralEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [claimableAmount, setClaimableAmount] = useState(0);
    const [claimableAmountOnChain, setClaimableOnChain] = useState(0);
    const [claimableAmountOffChain, setClaimableOffChain] = useState(0);
    const [claiming, setClaiming] = useState(false);

    const [referralData, setReferralData] = useState([]); // Consolidated List
    const [walletToEmailMap, setWalletToEmailMap] = useState({}); // Map wallet -> email

    // 1. Fetch Referral History (Events + Backend Data + Vesting Schedules)
    useEffect(() => {
        if (!wallet || !crowdsaleContract) return;

        const fetchHistory = async () => {
            setLoadingEvents(true);

            // A. Vesting Schedules (ON-CHAIN TRUTH)
            let onChainEvents = [];
            try {
                const schedules = await crowdsaleContract.getVestingSchedules(wallet.accounts[0].address);

                onChainEvents = schedules.map((s, index) => {
                    const releaseTime = Number(s.releaseTime);
                    const unlockDate = new Date(releaseTime * 1000);
                    const now = Date.now();
                    const isUnlocked = now >= unlockDate.getTime();

                    const myAddress = wallet.accounts[0].address.toLowerCase();
                    const buyerAddress = s.relatedBuyer.toLowerCase();
                    const type = (buyerAddress === myAddress) ? 'Bienvenida (1%)' : 'Referido (3%)';

                    return {
                        id: `vesting-contract-${index}`,
                        date: new Date((releaseTime - (90 * 24 * 3600)) * 1000),
                        amount: parseFloat(ethers.formatUnits(s.bonusAmount, 18)),
                        unlockDate: unlockDate,
                        status: s.claimed ? 'claimed' : (isUnlocked ? 'unlocked' : 'locked'),
                        referee: s.relatedBuyer,
                        type: type,
                        origin: 'Contrato (Web3)',
                        platform: 'contract'
                    };
                });
            } catch (e) {
                console.error("Error fetching on-chain vesting:", e);
            }



            // A.2. Vesting Schedules (OFF-CHAIN / FIRESTORE)
            try {
                const vestingRef = collection(db, 'vesting_schedules');
                const q = query(vestingRef, where('beneficiary', '==', wallet.accounts[0].address.toLowerCase()));

                const snap = await getDocs(q);
                const offChainSchedules = [];

                snap.forEach(doc => {
                    const d = doc.data();
                    const unlockDate = d.unlockDate.toDate(); // Firestore Timestamp
                    const now = new Date();
                    const isUnlocked = now >= unlockDate;

                    const typeLabel = d.type === 'referral_reward' ? 'Referido (3%)' : 'Bienvenida (1%)';

                    // Use status from Firestore (handles 'active', 'claimed', 'revoked')
                    let displayStatus = d.status || 'active';

                    // If active, determine if locked or unlocked
                    if (displayStatus === 'active') {
                        displayStatus = d.claimed ? 'claimed' : (isUnlocked ? 'unlocked' : 'locked');
                    }

                    offChainSchedules.push({
                        id: doc.id,
                        date: d.createdAt ? d.createdAt.toDate() : new Date(),
                        amount: d.bonusAmount,
                        unlockDate: unlockDate,
                        status: displayStatus,
                        referee: d.relatedBuyer,
                        type: typeLabel,
                        origin: 'Plataforma (CLP)',
                        platform: 'backend',
                        revokedAt: d.revokedAt ? d.revokedAt.toDate() : null,
                        reason: d.reason || null
                    });
                });

                // Merge Both
                const allEvents = [...onChainEvents, ...offChainSchedules].sort((a, b) => b.unlockDate - a.unlockDate);
                setReferralEvents(allEvents);

                // Calculate Claimables
                const onChainClaimable = onChainEvents.filter(e => e.status === 'unlocked' && e.origin !== 'Plataforma (CLP)').reduce((acc, curr) => acc + curr.amount, 0);
                const offChainClaimable = offChainSchedules.filter(e => e.status === 'unlocked').reduce((acc, curr) => acc + curr.amount, 0);

                setClaimableOnChain(onChainClaimable);
                setClaimableOffChain(offChainClaimable);

                setReferralEvents(allEvents);

            } catch (e) {
                console.error("Error fetching off-chain vesting:", e);
            }

            // B. Backend Data (Independent)
            try {
                const getReferralsFn = httpsCallable(functions, 'obtenerMisReferidos');
                const res = await getReferralsFn({ walletAddress: wallet.accounts[0].address, walletHistory: userData?.walletHistory || [] });

                if (res.data && res.data.success && res.data.data) {
                    const { leads, registered } = res.data.data;
                    const unifiedList = [];

                    // 1. Add Pending Leads
                    leads.forEach(lead => {
                        if (lead.status === 'pending') {
                            unifiedList.push({
                                email: lead.email,
                                status: 'Invitado',
                                date: lead.createdAt || new Date(),
                                wallet: null
                            });
                        }
                    });

                    // 2. Add Registered Users (Check if Investor)
                    registered.forEach(user => {
                        // NEW WAY: Backend Authority (user.isInvestor)
                        // Ya no necesitamos cruzar con eventos porque el backend hace el trabajo sucio.
                        const isInvestor = user.isInvestor === true;

                        unifiedList.push({
                            email: user.email,
                            status: isInvestor ? 'Inversionista' : 'Registrado',
                            date: user.createdAt || new Date(),
                            wallet: user.walletAddress
                        });
                    });

                    setReferralData(unifiedList.sort((a, b) => new Date(b.date) - new Date(a.date)));
                }
            } catch (backendErr) {
                console.warn("Error fetching backend referrals:", backendErr);
            } finally {
                setLoadingEvents(false);
            }
        };

        fetchHistory();
    }, [wallet, crowdsaleContract]);


    // 1B. Fetch Emails for Wallet Addresses (usando Cloud Function)
    useEffect(() => {
        if (referralEvents.length === 0) return;

        const fetchEmails = async () => {
            const wallets = new Set();

            // Collect all unique wallet addresses from events
            referralEvents.forEach(ev => {
                if (ev.referee) wallets.add(ev.referee.toLowerCase());
            });

            // Also add referredBy wallet if exists
            if (userData?.referredBy) {
                wallets.add(userData.referredBy.toLowerCase());
            }

            if (wallets.size === 0) return;

            console.log('[Bonos] Buscando emails para', wallets.size, 'wallets');

            try {
                // USAR CLOUD FUNCTION con permisos de admin
                const getEmailsFn = httpsCallable(functions, 'obtenerEmailsDeWallets');
                const response = await getEmailsFn({ walletAddresses: Array.from(wallets) });

                if (response.data.success) {
                    const emailMap = response.data.emailMap;
                    console.log('[Bonos] Emails mapeados:', Object.keys(emailMap).length);
                    setWalletToEmailMap(emailMap);

                    // Log which wallets don't have emails
                    const stillMissing = Array.from(wallets).filter(w => !emailMap[w]);
                    if (stillMissing.length > 0) {
                        console.warn('[Bonos] Wallets sin email encontrado:', stillMissing);
                    }
                } else {
                    console.error('[Bonos] Error en respuesta de Cloud Function');
                }
            } catch (error) {
                console.error('Error fetching emails via Cloud Function:', error);
            }
        };




        fetchEmails();
    }, [referralEvents, userData]);

    // 2. Claim Functions
    const handleClaim = async () => {
        setClaiming(true);
        try {
            let claimedSomething = false;

            // A. Claim On-Chain (Smart Contract)
            if (claimableAmountOnChain > 0) {
                try {
                    console.log("Claiming On-Chain...");
                    const tx = await crowdsaleContract.claimVestedTokens();
                    await tx.wait();
                    claimedSomething = true;
                } catch (e) {
                    console.error("On-Chain Claim Error:", e);
                }
            }

            // B. Claim Off-Chain (Backend)
            if (claimableAmountOffChain > 0) {
                try {
                    console.log("Claiming Off-Chain...");
                    const claimFn = httpsCallable(functions, 'reclamarBonosOffchain');
                    await claimFn();
                    claimedSomething = true;
                } catch (e) {
                    console.error("Off-Chain Claim Error:", e);
                }
            }

            if (claimedSomething) {
                alert("¡Bonos reclamados exitosamente!");
                window.location.reload();
            } else {
                alert("No tienes bonos disponibles para reclamar en este momento.");
            }

        } catch (e) {
            console.error("General Claim Error:", e);
            alert("Ocurrió un error al intentar reclamar.");
        } finally {
            setClaiming(false);
        }
    };

    // Helper: Mask Address
    const maskAddress = (addr) => {
        if (!addr || addr.length < 10) return '---';
        return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
    };

    // Helper: Get Email or Masked Address
    const getDisplayName = (walletAddr) => {
        if (!walletAddr) return '---';
        const normalized = walletAddr.toLowerCase();
        const email = walletToEmailMap[normalized];

        console.log('[getDisplayName] Input:', walletAddr);
        console.log('[getDisplayName] Normalized:', normalized);
        console.log('[getDisplayName] Map has', Object.keys(walletToEmailMap).length, 'entries');
        console.log('[getDisplayName] Found email:', email);

        return email || maskAddress(walletAddr);
    };

    return (
        <div className="bonos-view fade-in">
            <h2 className="view-title">Historial de Bonos y Recompensas <span style={{ fontSize: '0.8rem', opacity: 0.5, marginLeft: '10px' }}>{VERSION_STRING}</span></h2>

            {/* Summary Widget */}
            <ReferralWidget />

            {/* Referral Status (Transparency) */}
            <div className="card" style={{ marginTop: '30px', padding: '25px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderLeft: '4px solid #00d2ff' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#e0e0e0' }}>Estado de Referencia</h3>
                    <p style={{ margin: '5px 0 0 0', opacity: 0.7, fontSize: '0.9rem' }}>
                        Esta cuenta está vinculada permanentemente a un referidor.
                    </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    {userData?.referredBy ? (
                        <div>
                            <span style={{ display: 'block', fontSize: '1.1rem', fontWeight: 'bold', color: '#4caf50' }}>
                                <FaCheckCircle style={{ marginRight: '6px' }} />
                                Vinculado
                            </span>
                            <span style={{ display: 'block', fontSize: '0.85rem', color: '#888', background: '#1a1a1a', padding: '4px 8px', borderRadius: '4px', marginTop: '5px' }}>
                                Referido por: {getDisplayName(userData.referredBy)}
                            </span>
                        </div>
                    ) : (
                        <div>
                            <span style={{ color: '#888', fontStyle: 'italic' }}>Sin referidor asignado</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Claims Section */}
            <div className="card" style={{ marginTop: '30px', padding: '30px', textAlign: 'center' }}>
                <h3>Gestión de Recompensas</h3>
                <p style={{ opacity: 0.8, marginBottom: '20px' }}>
                    Tus bonos se desbloquean automáticamente pasados los 90 días.
                </p>
                <button
                    className="action-button confirm"
                    onClick={handleClaim}
                    disabled={claiming}
                    style={{ fontSize: '1.2rem', padding: '15px 40px', opacity: claiming ? 0.7 : 1 }}
                >
                    {claiming ? 'Procesando...' : 'Reclamar Bonos Disponibles'}
                </button>
            </div>

            {/* NEW: Referral Funnel Table */}
            <div className="card" style={{ marginTop: '30px', overflowX: 'auto', borderTop: '4px solid #9c27b0' }}>
                <h3>📢 Mis Referidos (Embudo)</h3>
                <p style={{ fontSize: '0.9rem', opacity: 0.7, marginBottom: '20px' }}>
                    Sigue el estado de tus invitados desde el registro hasta la inversión.
                </p>

                {loadingEvents ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Cargando datos...</div>
                ) : referralData.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', opacity: 0.7 }}>
                        No tienes referidos registrados aún.
                    </div>
                ) : (
                    <table className="assets-table">
                        <thead>
                            <tr>
                                <th>Usuario / Email</th>
                                <th>Fecha</th>
                                <th>Estado</th>
                                <th>Wallet</th>
                            </tr>
                        </thead>
                        <tbody>
                            {referralData.map((item, idx) => (
                                <tr key={idx}>
                                    <td>
                                        <span style={{ fontWeight: 'bold', color: item.status === 'Inversionista' ? '#00d2ff' : '#333' }}>
                                            {item.email || '---'}
                                        </span>
                                    </td>
                                    <td>{new Date(item.date).toLocaleDateString()}</td>
                                    <td>
                                        {item.status === 'Invitado' && (
                                            <span className="status-tag" style={{ background: 'rgba(255,255,255,0.1)', color: '#aaa' }}>
                                                ⏳ Invitado
                                            </span>
                                        )}
                                        {item.status === 'Registrado' && (
                                            <span className="status-tag" style={{ background: 'rgba(33, 150, 243, 0.15)', color: '#2196f3' }}>
                                                ✅ Registrado
                                            </span>
                                        )}
                                        {item.status === 'Inversionista' && (
                                            <span className="status-tag" style={{ background: 'rgba(76, 175, 80, 0.15)', color: '#4caf50' }}>
                                                Inversionista
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                        {item.wallet ? maskAddress(item.wallet) : '---'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* History Table */}
            <div className="card" style={{ marginTop: '30px', overflowX: 'auto' }}>
                <h3>Historial de Bonos Ganados</h3>
                {loadingEvents ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>Cargando historial de la Blockchain...</div>
                ) : referralEvents.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>
                        No tienes bonos registrados aún. ¡Comienza a invitar!
                    </div>
                ) : (
                    <table className="assets-table">
                        <thead>
                            <tr>
                                <th>Fecha (Desbloqueo)</th>
                                <th>Tipo</th>
                                <th>Origen</th>
                                <th>Referido</th>
                                <th>Bono</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {referralEvents.map((ev, index) => (
                                <tr key={index} style={{ opacity: ev.status === 'revoked' ? 0.6 : 1 }}>
                                    <td>{ev.unlockDate ? new Date(ev.unlockDate).toLocaleDateString() : '-'}</td>
                                    <td>{ev.type}</td>
                                    <td>{ev.origin}</td>
                                    <td style={{ fontSize: '0.9rem' }}>{getDisplayName(ev.referee)}</td>
                                    <td>{ev.amount}</td>
                                    <td>
                                        {ev.status === 'locked' && (
                                            <span className="status-tag" style={{ background: 'rgba(255, 152, 0, 0.15)', color: '#ff9800' }}>
                                                Bloqueado
                                            </span>
                                        )}
                                        {ev.status === 'unlocked' && (
                                            <span className="status-tag" style={{ background: 'rgba(76, 175, 80, 0.15)', color: '#4caf50' }}>
                                                Disponible
                                            </span>
                                        )}
                                        {ev.status === 'claimed' && (
                                            <span className="status-tag" style={{ background: 'rgba(33, 150, 243, 0.15)', color: '#2196f3' }}>
                                                ✅ Reclamado
                                            </span>
                                        )}
                                        {ev.status === 'revoked' && (
                                            <span
                                                className="status-tag"
                                                style={{ background: 'rgba(244, 67, 54, 0.15)', color: '#f44336', cursor: 'help' }}
                                                title={`Cancelado: ${ev.reason === 'insufficient_holding' ? 'Vendiste tus tokens antes del vesting' : 'El comprador vendió los tokens'}`}
                                            >
                                                ❌ Cancelado
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default BonosView;
